import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { axiosInstance } from "../../../utils/axios";
import useAuth from "../../../hooks/useAuth";
import { useNavigate } from "react-router-dom";

import {
  Avatar as MuiAvatar,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  TextField,
  Chip as MuiChip,
  CircularProgress,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Tooltip,
  Typography,
  InputBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  Snackbar,
  Alert,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Add as AddIcon,
  Lock,
  Search as SearchIcon,
} from "@mui/icons-material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";
import { useTranslation } from "react-i18next";

// GraphQL mutations
const INVITE_TEAM_MEMBER_MUTATION = `
  mutation InviteTeamMember($input: InviteTeamMemberInput!) {
    inviteTeamMember(input: $input) {
      success
      message
      teamMember {
        id
        email
        role
        status
      }
    }
  }
`;

const REMOVE_TEAM_MEMBER_MUTATION = `
  mutation RemoveTeamMember($input: RemoveTeamMemberInput!) {
    removeTeamMember(input: $input)
  }
`;

const GET_TEAM_MEMBERS = `
  query GetTeamMembers($teamId: ID!) {
    getTeamMembers(teamId: $teamId) {
      id
      userId
      email
      name
      role
      status
      user {
        id
        name
        email
        avatar
      }
      inviteToken
      inviteExpires
      addedBy {
        id
        name
        email
      }
      addedAt
    }
  }
`;

const GET_USERS = `
  query GetUsers {
    getUsers {
      id
      name
      email
    }
  }
`;

const Paper = styled(MuiPaper)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Chip = styled(MuiChip)`
  ${spacing};
  background: ${(props) => props.active && props.theme.palette.success.main};
  background: ${(props) => props.pending && props.theme.palette.warning.main};
  color: ${(props) =>
    (props.active || props.pending) && props.theme.palette.common.white};
`;
const Avatar = styled(MuiAvatar)`
  background: ${(props) => props.theme.palette.primary.main};
`;

const Spacer = styled.div`
  flex: 1 1 100%;
`;

const ToolbarTitle = styled.div`
  min-width: 200px;
`;

function Members({ team, onUpdate }) {
  const [members, setMembers] = useState(team?.members || []);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("email");
  const [viewStyle, setViewStyle] = useState("List");
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    members: [],
  });
  const [settings, setSettings] = useState({
    userImpersonation: team?.settings?.userImpersonation || false,
  });
  const [impersonatingMemberId, setImpersonatingMemberId] = useState(null);

  // State for user selection in add members dialog
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsersToInvite, setSelectedUsersToInvite] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Auth hook and navigation
  const { impersonateUser, user: currentUser } = useAuth();
  const navigate = useNavigate();

  // t function for translation
  const { t } = useTranslation();

  // Search state and methods
  const [searchQuery, setSearchQuery] = useState("");
  const filteredMembers = members.filter((member) => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();
    return (
      (member.email && member.email.toLowerCase().includes(query)) ||
      (member.user &&
        member.user.name &&
        member.user.name.toLowerCase().includes(query))
    );
  });

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.post("/graphql", {
          query: GET_TEAM_MEMBERS,
          variables: { teamId: team.id },
        });

        if (response.data?.getTeamMembers) {
          setMembers(response.data.getTeamMembers);
        } else {
          setMembers([]);
        }
      } catch (error) {
        console.error("Error fetching team members:", error);
        setNotification({
          open: true,
          message: "Error fetching team members",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    if (team?.id) {
      fetchTeamMembers();
    }
  }, [team]);

  // Functions to handle table operations
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = filteredMembers.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Empty rows logic
  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - filteredMembers.length)
      : 0;

  // Fetch available users for invite dropdown
  const fetchAvailableUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await axiosInstance.post("/graphql", {
        query: GET_USERS,
      });

      if (response.data?.getUsers) {
        // Filter out users who are already team members
        // Check by both email and userId to cover all cases
        const existingMemberEmails = new Set(
          members
            .flatMap((m) => [
              m.email?.toLowerCase(),
              m.user?.email?.toLowerCase(),
            ])
            .filter(Boolean)
        );
        const existingMemberUserIds = new Set(
          members.map((m) => m.userId).filter(Boolean)
        );

        const filteredUsers = response.data.getUsers.filter((user) => {
          // Check if user's email is already in the team
          const emailExists = existingMemberEmails.has(
            user.email?.toLowerCase()
          );
          // Check if user's ID matches any team member's userId
          const userIdExists = existingMemberUserIds.has(user.id);
          // Exclude if either matches
          return !emailExists && !userIdExists;
        });

        setAvailableUsers(filteredUsers);
      } else {
        setAvailableUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Dialog handlers
  const handleInviteDialogOpen = () => {
    setInviteDialogOpen(true);
    setSelectedUsersToInvite([]);
    fetchAvailableUsers();
  };

  const handleInviteDialogClose = () => {
    setInviteDialogOpen(false);
    setSelectedUsersToInvite([]);
  };

  // Team member operations
  const handleInviteMembers = async () => {
    if (selectedUsersToInvite.length === 0) {
      handleNotification("Please select at least one user to add", "warning");
      return;
    }

    try {
      setLoading(true);

      // Add all selected users as members
      const invitePromises = selectedUsersToInvite.map((user) =>
        axiosInstance.post("/graphql", {
          query: INVITE_TEAM_MEMBER_MUTATION,
          variables: {
            input: {
              teamId: team.id,
              email: user.email,
              role: "member",
            },
          },
        })
      );

      const results = await Promise.all(invitePromises);

      // Process results and update member list
      const newMembers = results
        .filter((res) => !res.errors && res.data?.inviteTeamMember?.teamMember)
        .map((res) => res.data.inviteTeamMember.teamMember);

      if (newMembers.length > 0) {
        setMembers([...members, ...newMembers]);
      }

      handleNotification(
        `Successfully added ${newMembers.length} member(s)`,
        "success"
      );
      handleInviteDialogClose();
    } catch (error) {
      console.error("Error adding members:", error);
      handleNotification("Failed to add members", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMembers = (membersToDelete) => {
    setConfirmDelete({
      open: true,
      members: membersToDelete,
    });
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);

      const deletePromises = confirmDelete.members.map((member) =>
        axiosInstance.post("/graphql", {
          query: REMOVE_TEAM_MEMBER_MUTATION,
          variables: {
            input: {
              teamId: team.id,
              memberId: member.id,
            },
          },
        })
      );

      await Promise.all(deletePromises);

      // Remove deleted members from state
      const memberIdsToDelete = confirmDelete.members.map((m) => m.id);
      setMembers(members.filter((m) => !memberIdsToDelete.includes(m.id)));
      setSelected([]);

      handleNotification(
        `Successfully removed ${confirmDelete.members.length} member(s)`,
        "success"
      );
    } catch (error) {
      console.error("Error removing members:", error);
      handleNotification("Failed to remove members", "error");
    } finally {
      setLoading(false);
      setConfirmDelete({ open: false, members: [] });
    }
  };

  // Setting toggle handlers
  const handleSettingChange = (event) => {
    const { name, checked } = event.target;
    setSettings({ ...settings, [name]: checked });
  };

  // Save settings handler
  const handleSaveSettings = async () => {
    if (!onUpdate) {
      handleNotification("Unable to save settings", "error");
      return;
    }

    try {
      setLoading(true);
      await onUpdate({
        settings: {
          ...team?.settings,
          userImpersonation: settings.userImpersonation,
        },
      });
      handleNotification("Team settings saved successfully", "success");
    } catch (error) {
      console.error("Error saving settings:", error);
      handleNotification("Failed to save settings", "error");
    } finally {
      setLoading(false);
    }
  };

  // Notification handling
  const handleNotification = (message, severity = "info") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  // Handle impersonation of a team member
  const handleImpersonate = async (member) => {
    // Check if impersonateUser function is available
    if (!impersonateUser) {
      handleNotification(
        "Impersonation feature is not available. Please refresh the page.",
        "error"
      );
      console.error("impersonateUser function is undefined");
      return;
    }

    // Check if user impersonation is enabled for the team
    if (!settings.userImpersonation) {
      handleNotification(
        "User impersonation is disabled for this team. Enable it in Team Settings.",
        "warning"
      );
      return;
    }

    // Check if member has a userId (required for impersonation)
    if (!member.userId) {
      handleNotification(
        "Cannot impersonate this member. User ID not available.",
        "error"
      );
      return;
    }

    // Check if trying to impersonate yourself
    if (currentUser?.id === member.userId) {
      handleNotification("You cannot impersonate yourself.", "warning");
      return;
    }

    try {
      setImpersonatingMemberId(member._id);
      await impersonateUser(member.userId);
      handleNotification(
        `Now impersonating ${member.name || member.email}`,
        "success"
      );
      // Redirect to dashboard after successful impersonation
      setTimeout(() => {
        navigate("/private");
      }, 500);
    } catch (error) {
      console.error("Impersonation failed:", error);
      handleNotification(
        error.message || "Failed to impersonate user. Please try again.",
        "error"
      );
    } finally {
      setImpersonatingMemberId(null);
    }
  };

  // Table header cells
  const headCells = [
    { id: "name", alignment: "left", label: "Full Name" },
    { id: "email", alignment: "left", label: "Email ID" },
    { id: "status", alignment: "left", label: "Status" },
    { id: "actions", alignment: "right", label: "Actions" },
  ];

  // Table header component
  const EnhancedTableHead = (props) => {
    const {
      onSelectAllClick,
      order,
      orderBy,
      numSelected,
      rowCount,
      onRequestSort,
    } = props;

    const createSortHandler = (property) => (event) => {
      onRequestSort(event, property);
    };

    return (
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{ "aria-label": "select all members" }}
            />
          </TableCell>
          {headCells.map((headCell) => (
            <TableCell
              key={headCell.id}
              align={headCell.alignment}
              padding="normal"
              sortDirection={orderBy === headCell.id ? order : false}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
              </TableSortLabel>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  // Table toolbar component
  const EnhancedTableToolbar = (props) => {
    const { numSelected, onDeleteSelected } = props;
    const selectedMembers = members.filter((m) => selected.includes(m.id));

    return (
      <Toolbar
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
          ...(numSelected > 0 && {
            bgcolor: (theme) =>
              alpha(
                theme.palette.primary.main,
                theme.palette.action.activatedOpacity
              ),
          }),
        }}
      >
        <ToolbarTitle>
          {numSelected > 0 ? (
            <Typography color="inherit" variant="subtitle1">
              {numSelected} selected
            </Typography>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 1,
                  backgroundColor: (theme) =>
                    alpha(theme.palette.common.black, 0.05),
                  "&:hover": {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.common.black, 0.1),
                  },
                  width: "100%",
                  maxWidth: 300,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    pointerEvents: "none",
                    pl: 2,
                  }}
                >
                  <SearchIcon />
                </Box>
                <InputBase
                  sx={{
                    pl: 6,
                    py: 1,
                    width: "100%",
                  }}
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Box>
            </Box>
          )}
        </ToolbarTitle>
        <Spacer />
        {numSelected > 0 ? (
          <Tooltip title="Delete">
            <IconButton onClick={() => handleDeleteMembers(selectedMembers)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleInviteDialogOpen}
          >
            Invite
          </Button>
        )}
      </Toolbar>
    );
  };

  return (
    <Box>
      <Typography variant="h3" gutterBottom>
        Team Members
      </Typography>
      <Typography color="textSecondary" mb={4}>
        Users that are in the team
      </Typography>

      <Paper sx={{ mb: 4 }}>
        <EnhancedTableToolbar
          numSelected={selected.length}
          onDeleteSelected={() => {
            const selectedMembers = members.filter((m) =>
              selected.includes(m.id)
            );
            handleDeleteMembers(selectedMembers);
          }}
        />

        <TableContainer>
          <Table size="medium">
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={filteredMembers.length}
            />
            <TableBody>
              {filteredMembers
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((member, index) => {
                  const isItemSelected = isSelected(member.id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={member.id}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          inputProps={{ "aria-labelledby": labelId }}
                          onClick={(event) => handleClick(event, member.id)}
                        />
                      </TableCell>
                      <TableCell id={labelId} scope="row">
                        <Box display="flex" alignItems="center">
                          <Avatar src={member.user?.avatar || null}>
                            {member.name
                              ? member.name.charAt(0).toUpperCase()
                              : member.email &&
                                member.email.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box ml={2}>
                            {member.name ||
                              member.email.split("@")[0] ||
                              "Invited User"}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>
                        {member.status === "active" ? (
                          <Chip label="Active" size="small" color="success" />
                        ) : (
                          <Chip label="Pending" size="small" color="warning" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {member.status === "active" && (
                          <Tooltip title="Impersonate">
                            <IconButton
                              size="small"
                              onClick={() => handleImpersonate(member)}
                              disabled={impersonatingMemberId === member._id}
                            >
                              {impersonatingMemberId === member._id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Lock />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteMembers([member])}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredMembers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Team Settings
        </Typography>

        <Box mt={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Box>
              <Typography variant="subtitle1">User Impersonation</Typography>
              <Typography variant="body2" color="textSecondary">
                Allows your team Owners/Admins to temporarily sign in as you
              </Typography>
            </Box>
            <Switch
              name="userImpersonation"
              checked={settings.userImpersonation}
              onChange={handleSettingChange}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveSettings}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Settings"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Add Members Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={handleInviteDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Team Members</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select users to add to your team. You can select multiple users at
            once.
          </DialogContentText>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={availableUsers}
                getOptionLabel={(option) =>
                  option.name
                    ? `${option.name} (${option.email})`
                    : option.email
                }
                value={selectedUsersToInvite}
                onChange={(_, newValue) => setSelectedUsersToInvite(newValue)}
                loading={loadingUsers}
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    <Box>
                      <Typography variant="body1">
                        {option.name || "No Name"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.email}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Users"
                    placeholder={
                      selectedUsersToInvite.length === 0
                        ? "Search and select users..."
                        : ""
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.name || option.email}
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
                noOptionsText={
                  loadingUsers
                    ? "Loading users..."
                    : availableUsers.length === 0
                    ? "No users available to invite"
                    : "No matching users"
                }
              />
            </Grid>

            {selectedUsersToInvite.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  {selectedUsersToInvite.length} user(s) selected
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleInviteDialogClose} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleInviteMembers}
            disabled={loading || selectedUsersToInvite.length === 0}
          >
            {loading
              ? "Adding..."
              : `Add ${
                  selectedUsersToInvite.length > 0
                    ? `(${selectedUsersToInvite.length})`
                    : ""
                }`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, members: [] })}
      >
        <DialogTitle>Confirm Remove</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDelete.members.length === 1
              ? `Are you sure you want to remove ${
                  confirmDelete.members[0]?.email || "this member"
                } from the team?`
              : `Are you sure you want to remove ${confirmDelete.members.length} members from the team?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDelete({ open: false, members: [] })}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// Sort function
function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function descendingComparator(a, b, orderBy) {
  // Handle special cases like user names
  if (orderBy === "name") {
    const aName = a.user?.name || "";
    const bName = b.user?.name || "";
    return bName.localeCompare(aName);
  }

  // Default comparison
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

export default Members;
